import { useState } from "react";
import { Download, CheckCircle2, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lead-magnet section for the Landing page.
 * Captures name + email, posts to /api/leads/checklist, then reveals the
 * download link. Zero-dependency success state (no page reload).
 */
export function ChecklistLeadMagnet() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong");
      }
      const data = await res.json();
      setDownloadUrl(`${BACKEND_URL}${data.download_url}`);
      setStatus("success");
    } catch (err) {
      setError(err.message || "Could not submit — please try again.");
      setStatus("error");
    }
  };

  return (
    <section
      id="checklist"
      className="mx-auto max-w-6xl px-6 pb-24"
      aria-labelledby="checklist-heading"
      data-testid="checklist-section"
    >
      <div className="grid md:grid-cols-2 gap-10 items-center rounded-[2rem] bg-white border border-amber-200 shadow-[0_20px_60px_rgba(245,158,11,0.10)] overflow-hidden">
        {/* Preview card */}
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-white p-10 h-full min-h-[380px] flex flex-col justify-center">
          <div className="relative mx-auto w-64 h-80 bg-white rounded-2xl shadow-xl border border-stone-200 p-5 rotate-[-3deg] hover:rotate-0 transition-transform">
            <div className="text-[9px] uppercase tracking-[0.18em] font-semibold text-amber-600">HomeSchool Hub · Free</div>
            <div className="mt-2 font-display font-bold text-stone-900 leading-tight text-lg">Florida Homeschool Compliance Checklist</div>
            <div className="mt-4 space-y-2">
              {[
                "Notice of Intent",
                "Portfolio requirements",
                "180 days / 720 hours",
                "Annual evaluation",
                "Scholarships & funding",
                "Records to keep",
                "Terminating education",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2 text-[11px] text-stone-700">
                  <span className="w-3 h-3 rounded border border-amber-400 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
            <div className="absolute bottom-3 right-4 text-[9px] text-stone-400">PDF · 1 page</div>
          </div>
          <div className="absolute top-8 right-8 bg-amber-500 text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 rounded-full rotate-6">
            Free download
          </div>
        </div>

        {/* Form */}
        <div className="p-10 md:p-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600 flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> Free 1-page checklist
          </div>
          <h2 id="checklist-heading" className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
            The Florida Homeschool Compliance Checklist.
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Every requirement under FL Statute 1002.41 — Notice of Intent, portfolio rules,
            attendance minimums, annual evaluations, and every scholarship worth applying for.
            Print it, tick it, done.
          </p>

          {status === "success" ? (
            <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6" data-testid="checklist-success">
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <CheckCircle2 className="w-5 h-5" />
                Check your inbox — a copy is on the way.
              </div>
              <p className="mt-2 text-sm text-emerald-800/80">Or grab it right now:</p>
              <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="checklist-download-btn">
                <Button className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-6 py-5">
                  <Download className="w-4 h-4 mr-2" />
                  Download the checklist (PDF)
                </Button>
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-3" data-testid="checklist-form">
              <Input
                type="text"
                required
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl border-stone-200"
                data-testid="checklist-name-input"
              />
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-stone-200"
                data-testid="checklist-email-input"
              />
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="checklist-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={status === "loading"}
                className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base"
                data-testid="checklist-submit"
              >
                {status === "loading" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending your checklist…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Send me the checklist</>
                )}
              </Button>
              <p className="text-xs text-stone-500 text-center">
                No spam. Unsubscribe with one click. We&apos;ll email you occasional Florida homeschool tips.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
